<template>
  <div class="section">
    <div class="container custom-container">
      <h1 class="title">New Simulation</h1>
      <p>Check the FAQ for additional details on simulation setup.</p>

      <StepIndicator
        v-bind:value="activeStep"
        v-on:setStep="setStep"
        v-bind:errorSteps="missingEntries"
      />

      <!-- Step 1: Device Setup -->
      <div v-show="activeStep === 0">
        <h2 class="title is-4">Device Setup</h2>

        <div class="columns">
          <div class="column is-6">
            <!-- Title -->
            <div class="field">
              <label class="label">
                Title
                <span class="has-text-warning" v-if="missingEntries[0].includes('Title')">
                  <font-awesome-icon icon="exclamation-triangle" title="Missing title." />
                </span>
              </label>
              <p class="control">
                <input
                  class="input"
                  v-model="simTitle"
                  type="text"
                  placeholder="Text input"
                  id="step1-simulation-title"
                />
              </p>
            </div>
            <!-- Description -->
            <div class="field">
              <label class="label">Description (optional)</label>
              <p class="control">
                <textarea
                  class="textarea"
                  v-model="simDescription"
                  placeholder="Text input"
                  id="step1-simulation-description"
                ></textarea>
              </p>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column is-3">
            <!-- Pick Upload or Library Mode -->
            <label class="label">Device Geometry</label>
            <FieldRadio v-model="optionDevice" name="mode" v-bind:options="deviceOptions" />
          </div>
          <div class="column">
            <!-- Upload or Library Component -->
            <component
              :is="optionDevice"
              v-on:onGeometry="onGeometry"
              v-on:onStent="onStent"
              v-on:onInsulation="onInsulation"
              v-on:onTip="onTip"
              v-on:fileUpload="fileUpload"
            ></component>
          </div>
        </div>

        <!-- Device Location -->
        <div class="columns">
          <div class="column is-3">
            <label class="label">
              Device Location
              <font-awesome-icon
                icon="info-circle"
                title="For a custom location, define the coordinate of the device isoceter. Default implant position is: (X = 190 mm, Y = -25 mm, Z = 0 mm) for Polarizations Circular 1 and Linear 1; and (X = -190 mm, Y = -25 mm, Z = 0 mm) for Polarizations Circular 2 and Linear 2. Final device position may be adjusted to fit inside the ASTM phantom volume. Check the final location used in the results section."
                style="cursor: help;"
              />
            </label>
            <FieldRadio
              v-model="optionLocation"
              name="locationMode"
              v-bind:options="locationOptions"
            />
          </div>
          <div v-show="optionLocation == 'Custom'" class="column">
            <label class="label">Coordinates</label>
            <Field
              v-model="deviceLocation[0]"
              name="x"
              units="mm"
              :vmin="-customLocationBoundaryX"
              :vmax="customLocationBoundaryX"
              id="step1-coordinate-x"
            />
          </div>
          <div v-show="optionLocation == 'Custom'" class="column">
            <label class="label has-text-white">Coordinates y</label>
            <Field
              v-model="deviceLocation[1]"
              name="y"
              units="mm"
              :vmin="-customLocationBoundaryY"
              :vmax="customLocationBoundaryY"
              id="step1-coordinate-y"
            />
          </div>
          <div v-show="optionLocation == 'Custom'" class="column">
            <label class="label has-text-white">Coordinates z</label>
            <Field
              v-model="deviceLocation[2]"
              name="z"
              units="mm"
              :vmin="-customLocationBoundaryZ"
              :vmax="customLocationBoundaryZ"
              id="step1-coordinate-z"
            />
          </div>
        </div>

        <!-- Geometric Properties -->
        <div v-if="optionDevice === 'Library'">
          <hr />
          <div class="columns">
            <div v-if="optionGeometry === 0" class="column is-3">
              <h2 class="title is-4">Stent 1</h2>
              <h3 class="title is-5">Geometric Properties</h3>
              <Field
                name="Diameter"
                v-model="stentOneDiameter"
                units="mm"
                :vmin="1"
                :vmax="10"
                id="step1-device-diameter"
              />
              <Field
                name="Length"
                v-model="stentOneLength"
                units="mm"
                :vmin="5"
                :vmax="400"
                id="step1-device-length"
              />
              <Field
                name="Thickness"
                v-model="stentOneThickness"
                units="mm"
                :vmin="0.05"
                :vmax="1"
                help="The thickness has to be considered internal with respect to the stent diameter."
                id="step1-device-thickness"
              />
            </div>
            <div v-if="optionGeometry === 1" class="column is-3">
              <h2 class="title is-4">Rod</h2>
              <h3 class="title is-5">Geometric Properties</h3>
              <Field
                name="Diameter"
                v-model="rodDiameter"
                units="mm"
                :vmin="1"
                :vmax="10"
                id="step1-device-diameter"
              />
              <Field
                name="Length"
                v-model="rodLength"
                units="mm"
                :vmin="5"
                :vmax="400"
                id="step1-device-length"
              />
              <div
                class="columns"
                v-if="optionGeometry >= 1 && optionInsulation >= 1 && optionTip >= 1"
              >
                <div class="column">
                  <Field
                    :name="tipOneLengthLabel"
                    v-model="rodTipOne"
                    units="mm"
                    :vmin="0.5"
                    :vmax="rodTipOneMax"
                    id="step1-device-tip-one-length"
                  />
                </div>
                <div class="column" v-if="optionTip > 1">
                  <Field
                    name="Tip 2 Length"
                    v-model="rodTipTwo"
                    units="mm"
                    :vmin="0.5"
                    v-bind:vmax="rodTipTwoMax"
                    id="step1-device-tip-two-length"
                  />
                </div>
              </div>
            </div>
            <div v-if="optionInsulation == 1 || optionInsulation == 3" class="column is-3">
              <h2 class="title is-4 has-text-white">Insulation 1</h2>
              <h3 class="title is-5">{{insulationTitle}}</h3>
              <Field
                name="Thickness"
                v-model="insulationOneThickness"
                units="mm"
                :vmin="0.5"
                :vmax="10"
                id="step1-insulation-thickness"
              />
            </div>
            <div v-if="optionGeometry === 0 && optionStent === 'double'" class="column is-3">
              <h3 class="title is-4">Stent 2</h3>
              <h3 class="title is-5">Geometric Properties</h3>
              <Field
                name="Diameter"
                v-model="stentTwoDiameter"
                units="mm"
                :vmin="stentTwoDiameterMin"
                :vmax="10"
                help="At least twice the sum of the diameter of the first stent plus insulation (of the first stent) and the thickness of the second stent."
                id="step1-device-two-diameter"
              />
              <Field
                name="Length"
                v-model="stentTwoLength"
                units="mm"
                :vmin="5"
                :vmax="400"
                id="step1-device-two-length"
              />
              <Field
                name="Thickness"
                v-model="stentTwoThickness"
                units="mm"
                :vmin="0.05"
                :vmax="1"
                id="step1-device-two-thickness"
              />
              <Field
                name="Overlap"
                v-model="stentTwoOverlap"
                units="mm"
                :vmin="1"
                v-bind:vmax="stentTwoOverlapMax"
                id="step1-device-two-overlap"
              />
            </div>
            <div v-if="optionInsulation >= 2" class="column is-3">
              <h2 class="title is-4 has-text-white">Insulation 2</h2>
              <h3 class="title is-5">Insulation</h3>
              <Field
                name="Thickness"
                v-model="insulationTwoThickness"
                units="mm"
                :vmin="0.5"
                :vmax="10"
                id="step1-insulation-two-thickness"
              />
            </div>
          </div>
        </div>
        <hr />
        <StepPreviousNext v-bind:forStep="0" v-on:setStep="setStep" />
      </div>

      <!-- Step 2: Device Material -->
      <div v-show="activeStep === 1">
        <h2 class="title is-4">{{deviceName}}</h2>
        <div class="columns">
          <div class="column">
            <h3 class="title is-5">Material</h3>
            <FieldSelect
              name="Material"
              value="custom"
              v-bind:options="materials"
              @selected="deviceMaterialSelect"
              id="step2-device-material-select"
            />
            <Field
              name="Density"
              v-model="deviceDensity"
              units="kg/m<sup>3</sup>"
              :vpositive="true"
            />
          </div>
          <div class="column">
            <h3 class="title is-5">Electric Properties</h3>
            <Field name="Conductivity" v-model="deviceConductivity" units="S/m" :vpositive="true" />
            <Field name="Relative Permittivity" v-model="devicePermittivity" units="" :vpositive="true" />
          </div>
          <div class="column">
            <h3 class="title is-5">Thermal Properties</h3>
            <Field
              name="Thermal Conductivity"
              v-model="deviceThermalConductivity"
              units="W/(m&middot;&deg;C)"
              :vpositive="true"
            />
            <Field
              name="Heat Capacity"
              v-model="deviceHeatCapacity"
              units="J/(kg&middot;&deg;C)"
              :vpositive="true"
            />
          </div>
          <div class="column"></div>
        </div>
        <!-- Insulation 1 -->
        <div v-if="optionDevice === 'Library' && (optionInsulation > 0)">
          <h2 class="title is-4 breatheTop">{{insulationTitle}}</h2>
          <div class="columns">
            <div class="column is-3">
              <h3 class="title is-5">Material</h3>
              <FieldSelect
                name="Material"
                value="custom"
                v-bind:options="materials"
                @selected="insulationOneMaterialSelect"
                id="step2-insulation-material-select"
              />
              <Field
                name="Density"
                v-model="insulationOneDensity"
                units="kg/m<sup>3</sup>"
                :vpositive="true"
              />
            </div>
            <div class="column is-3">
              <h3 class="title is-5">Electric Properties</h3>
              <Field
                name="Conductivity"
                v-model="insulationOneConductivity"
                units="S/m"
                :vpositive="true"
              />
              <Field
                name="Relative Permittivity"
                v-model="insulationOnePermittivity"
                units=""
                :vpositive="true"
              />
            </div>
            <div class="column is-3">
              <h3 class="title is-5">Thermal Properties</h3>
              <Field
                name="Thermal Conductivity"
                v-model="insulationOneThermalConductivity"
                units="W/(m&middot;&deg;C)"
                :vpositive="true"
              />
              <Field
                name="Heat Capacity"
                v-model="insulationOneHeatCapacity"
                units="J/(kg&middot;&deg;C)"
                :vpositive="true"
              />
            </div>
          </div>
        </div>
        <hr />
        <StepPreviousNext v-bind:forStep="1" v-on:setStep="setStep" />
      </div>

      <!-- Step 3: Simulation Setup -->
      <div v-show="activeStep === 2">
        <Coil @onFrequency="onFrequency" @onPower="onPower" @onPolarization="onPolarization" />
        <!-- Thermal Simulation -->
        <h2 class="title is-4">Thermal Simulation</h2>
        <div class="columns">
          <div class="column is-3">
            <FieldSelect
              name="Duration (seconds)"
              @selected="optionThermalSelect"
              v-bind:options="thermalOptions"
              v-bind:value="optionThermal"
              id="step3-duration"
            />
          </div>
        </div>
        <!-- Reference Rod -->
        <h2 class="title is-4">
          Reference Rod
          <font-awesome-icon
            icon="info-circle"
            title="A control rod can be included in the simulation to ensure scaling for run to run variations, and to scale simulated results to measurements."
            style="cursor: help;"
          />
        </h2>
        <div class="columns">
          <div class="column is-3">
            <div class="field">
              <div class="control">
                <label class="checkbox my-margin-top">
                  <input type="checkbox" v-model="referenceRodChecked" id="step3-include-ref-rod" />
                  Include Reference Rod
                </label>
              </div>
            </div>
            <div v-if="referenceRodChecked" class="control my-half-space">
              <div class="select">
                <select v-model="referenceRodName" id="step3-ref-rod-select">
                  <option v-for="(opt, i) in referenceRodOptions" :value="opt" :key="i">{{ opt }}</option>
                </select>
              </div>
            </div>
          </div>
          <div v-if="referenceRodChecked" class="column is-3">
            <h3 class="title is-5">Geometric Properties</h3>
            <Field
              name="Diameter"
              v-model="referenceRodDiameter"
              units="mm"
              :vmin="1"
              :vmax="10"
              id="step3-ref-rod-diameter"
            />
            <Field
              name="Length"
              v-model="referenceRodLength"
              units="mm"
              :vmin="5"
              :vmax="400"
              id="step3-ref-rod-length"
            />
            <Field
              name="Thickness"
              v-if="referenceRodName == 'Hollow Rod'"
              v-model="referenceRodThickness"
              units="mm"
              :vmin="0.05"
              :vmax="referenceRodMaxThickness"
              id="step3-ref-rod-thickness"
            />
          </div>
          <div v-if="referenceRodChecked" class="column is-3">
            <h3 class="title is-5">Material</h3>
            <FieldSelect
              name="Material"
              value="custom"
              v-bind:options="materials"
              @selected="referenceRodMaterialSelect"
              id="step3-ref-rod-material-select"
            />
            <Field
              name="Density"
              v-model="referenceRodDensity"
              units="kg/m<sup>3</sup>"
              :vpositive="true"
            />
            <Field
              name="Conductivity"
              v-model="referenceRodConductivity"
              units="S/m"
              :vpositive="true"
            />
            <Field
              name="Relative Permittivity"
              v-model="referenceRodPermittivity"
              units=""
              :vpositive="true"
            />
            <Field
              name="Thermal Conductivity"
              v-model="referenceRodThermalConductivity"
              units="W/(m&middot;&deg;C)"
              :vpositive="true"
            />
            <Field
              name="Heat Capacity"
              v-model="referenceRodHeatCapacity"
              units="J/(kg&middot;&deg;C)"
              :vpositive="true"
            />
          </div>
          <div v-if="referenceRodChecked" class="column is-3">
            <h3 class="title is-5">
              Position
              <font-awesome-icon
                icon="info-circle"
                title="Define the coordinates of the reference rod isoceter."
                style="cursor: help;"
              />
            </h3>
            <Field
              v-model="referenceRodLocation[0]"
              name="x"
              units="mm"
              :vmin="-referenceRodBoundaryX"
              :vmax="referenceRodBoundaryX"
              id="step3-ref-rod-position-x"
            />
            <Field
              v-model="referenceRodLocation[1]"
              name="y"
              units="mm"
              :vmin="-referenceRodBoundaryY"
              :vmax="referenceRodBoundaryY"
              id="step3-ref-rod-position-y"
            />
            <Field
              v-model="referenceRodLocation[2]"
              name="z"
              units="mm"
              :vmin="-referenceRodBoundaryZ"
              :vmax="referenceRodBoundaryZ"
              id="step3-ref-rod-position-z"
            />
          </div>
        </div>
        <hr />
        <StepPreviousNext v-bind:forStep="2" v-on:setStep="setStep" />
      </div>

      <!-- Step 4: Probes -->
      <div v-show="activeStep === 3">
        <Probes @probeEvent="updateProbes" />
        <hr />
        <StepPreviousNext v-bind:forStep="3" v-on:setStep="setStep" />
      </div>

      <!-- Step 5: Run -->
      <div v-show="activeStep === 4">
        <div class="columns">
          <div class="column">
            <h3 class="title is-5">Summary</h3>
            <h4 class="title is-6">
              Device
              <span class="has-text-warning" v-if="missingEntries[0].length > 0 && !(missingEntries[0].length === 1 && missingEntries[0].includes('Insulation'))">
                <font-awesome-icon icon="exclamation-triangle" title="Missing inputs." />
              </span>
            </h4>
            <p class="desc">
              <span>Device</span>
              {{extendedDeviceName}}
            </p>
            <p class="desc">
              <span>Location</span>
              {{deviceLocationStr}}
            </p>
            <div v-if="optionDevice === 'Library'">
              <p class="desc" v-for="(geom, i) in deviceGeometry" :key="i">
                <span>{{geom[0]}}</span>
                {{geom[1]}}
              </p>
              <p class="desc" v-if="optionGeometry === 0 && optionStent === 'double'">
                <span>Overlap</span>
                {{stentTwoOverlap}} mm
              </p>
              <p class="desc" v-if="optionGeometry === 1 && optionInsulation >= 1 && optionTip > 0">
                <span>Tip(s)</span>
                {{rodTipOne}} mm
                <span v-if="optionTip > 1">; {{rodTipTwo}} mm</span>
              </p>
            </div>
          </div>
          <div class="column">
            <h3 class="title is-5 has-text-white">Summary</h3>
            <h4 class="title is-6">
              Insulation
              <span class="has-text-warning" v-if="missingEntries[0].includes('Insulation')">
                <font-awesome-icon icon="exclamation-triangle" title="Missing inputs." />
              </span>
            </h4>
            <div v-if="optionDevice === 'Library' && optionInsulation > 0">
              <p class="desc" v-if="optionInsulation === 1 || optionInsulation === 3">
                <span>Insulation</span>
                {{insulationOneThickness}} mm
              </p>
              <p class="desc" v-if="optionInsulation >= 2">
                <span>Insulation (2)</span>
                {{insulationTwoThickness}} mm
              </p>
            </div>
            <p v-else>n/a</p>
          </div>
          <div class="column">
            <h3 class="title is-5 has-text-white">Summary</h3>
            <h4 class="title is-6">
              Reference Rod
              <span class="has-text-warning" v-if="missingEntries[2].includes('Reference Rod Geometric Properties')">
                <font-awesome-icon icon="exclamation-triangle" title="Missing inputs." />
              </span>
            </h4>
            <div v-if="referenceRodChecked">
              <p class="desc">
                <span>Type</span>{{referenceRodName}}
              </p>
              <p class="desc">
                <span>Geometry</span>{{referenceRodGeometry}}
              </p>
              <p class="desc">
                <span>Location</span>{{referenceRodPosition}}
              </p>
            </div>
            <div v-else>
              n/a
            </div>
          </div>
          <div class="column">
            <h3 class="title is-5 has-text-white">Summary</h3>
            <h4 class="title is-6">Phantom</h4>
            <p class="desc">
              <span>Length</span>650 mm
            </p>
            <p class="desc">
              <span>Height</span>420 mm
            </p>
            <p class="desc">
              <span>Width</span>90 mm
            </p>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <h3 class="title is-5">Materials</h3>
            <h4 class="title is-6">
              Device
              <span class="has-text-warning" v-if="missingEntries[1].includes('Device Material')">
                <font-awesome-icon icon="exclamation-triangle" title="Missing inputs." />
              </span>
            </h4>
            <p class="desc">
              <span>Name</span>
              {{deviceMaterialName}}
            </p>
            <p class="desc">
              <span>Density</span>
              {{deviceDensity}} kg/m<sup>3</sup>
            </p>
            <p class="desc">
              <span>Conductivity</span>
              {{deviceConductivity}} S/m
            </p>
            <p class="desc">
              <span>Rel. Permittivity</span>
              {{devicePermittivity}}
            </p>
            <p class="desc">
              <span>Thermal Cond.</span>
              {{deviceThermalConductivity}} W/(m&middot;&deg;C)
            </p>
            <p class="desc">
              <span>Heat Capacity</span>
              {{deviceHeatCapacity}} J/(kg&middot;&deg;C)
            </p>
          </div>
          <div class="column">
            <h3 class="title is-5 has-text-white">Materials</h3>
            <h4 class="title is-6">
              Insulation
              <span class="has-text-warning" v-if="missingEntries[1].includes('Insulation Material')">
                <font-awesome-icon icon="exclamation-triangle" title="Missing inputs." />
              </span>
            </h4>
            <div v-if="optionDevice === 'Library' && optionInsulation > 0">
              <p class="desc">
                <span>Material</span>
                {{insulationOneMaterialName}}
              </p>
              <p class="desc">
                <span>Density</span>
                {{insulationOneDensity}} kg/m<sup>3</sup>
              </p>
              <p class="desc">
                <span>Conductivity</span>
                {{insulationOneConductivity}} S/m
              </p>
              <p class="desc">
                <span>Rel. Permittivity</span>
                {{insulationOnePermittivity}}
              </p>
              <p class="desc">
                <span>Thermal Cond.</span>
                {{insulationOneThermalConductivity}} W/(m&middot;&deg;C)
              </p>
              <p class="desc">
                <span>Heat Capacity</span>
                {{insulationOneHeatCapacity}} J/(kg&middot;&deg;C)
              </p>
            </div>
            <div v-else>n/a</div>
          </div>
          <div class="column">
            <h3 class="title is-5 has-text-white">Materials</h3>
            <h4 class="title is-6">
              Reference Rod
              <span class="has-text-warning" v-if="missingEntries[2].includes('Reference Rod Material Properties')">
                <font-awesome-icon icon="exclamation-triangle" title="Missing inputs." />
              </span>
            </h4>
            <div v-if="referenceRodChecked">
              <p class="desc">
                <span>Name</span>{{referenceRodMaterialName}}
              </p>
              <p class="desc">
                <span>Density</span>{{referenceRodDensity}} kg/m<sup>3</sup>
              </p>
              <p class="desc">
                <span>Conductivity</span>{{referenceRodConductivity}} S/m
              </p>
              <p class="desc">
                <span>Rel. Permittivity</span>{{referenceRodPermittivity}}
              </p>
              <p class="desc">
                <span>Thermal Cond.</span>{{referenceRodThermalConductivity}} W/(m&middot;&deg;C)
              </p>
              <p class="desc">
                <span>Heat Capacity</span>{{referenceRodHeatCapacity}} J/(kg&middot;&deg;C)
              </p>
            </div>
            <div v-else>
              n/a
            </div>
          </div>
          <div class="column">
            <h3 class="title is-5 has-text-white">Materials</h3>
            <h4 class="title is-6">Phantom</h4>
            <p class="desc">
              <span>Density</span>1011.32 kg/m<sup>3</sup>
            </p>
            <p class="desc">
              <span>Conductivity</span>0.47 S/m
            </p>
            <p class="desc">
              <span>Rel. Permittivity</span>80
            </p>
            <p class="desc">
              <span>Diffusivity</span>1.3E-7 m<sup>2</sup>/s
            </p>
            <p class="desc">
              <span>Heat Capacity</span>4150 J/(kg&middot;&deg;C)
            </p>
          </div>
        </div>
        <div class="columns">
          <div class="column is-3">
            <h3 class="title is-5">Setup</h3>
            <h4 class="title is-6">RF Coil</h4>
            <p class="desc">
              <span>Frequency</span>{{frequencyOptions[optionFrequency]}} MHz
            </p>
            <p class="desc">
              <span>Power</span>{{powerOptions[optionPower]}} W/kg
            </p>
            <p class="desc">
              <span>Polarization</span>{{polarizationOptions[optionPolarization]}}
            </p>
            <p class="desc">
              <span>Length</span>
              {{coilGeometricProperties[optionFrequency].length}} mm
            </p>
            <p class="desc">
              <span>Diameter</span>
              {{coilGeometricProperties[optionFrequency].diameter}} mm
            </p>
          </div>
          <div class="column is-3">
            <h3 class="title is-5 has-text-white">Setup</h3>
            <h4 class="title is-6">Thermal Simulation</h4>
            <p class="desc">
              <span>Duration</span>{{optionThermal}} s
            </p>
          </div>
          <div class="column is-3">
            <h3 class="title is-5 has-text-white">Setup</h3>
            <h4 class="title is-6">
              Probes
              <span class="has-text-warning" v-if="missingEntries[3].includes('Probe Coordinates')">
                <font-awesome-icon icon="exclamation-triangle" title="Missing inputs." />
              </span>
            </h4>
            <div v-if="probeCoordinates !== null">
              <p class="desc" v-for="(p, i) in probeCoordinates" :key="i">
                <span>Probe {{i+1}}</span>
                X={{p.X}}; Y={{p.Y}}; Z={{p.Z}}
              </p>
            </div>
            <div v-else>
              n/a
            </div>
          </div>
        </div>
        <hr />
        <h2 class="title is-4">Run Simulation</h2>
        <div v-if="canCreateSimulation">
          <div v-if="!isValidForm">
            <div class="level">
              <div class="level-left">
                <div class="level-item">
                  <button class="button is-warning" disabled>Run</button>
                </div>
                <div class="level-item">
                  <p>Disabled due to missing inputs.</p>
                </div>
              </div>
              <div class="level-right"></div>
            </div>
          </div>
          <div v-else>
            <div class="level">
              <div class="level-left">
                <div class="level-item">
                  <button
                    class="button is-success"
                    :disabled="isSubmitting"
                    v-on:click="submitSimulation"
                  >Run</button>
                </div>
                <div class="level-item">
                  <Loader v-bind:isLoading="isSubmitting" />
                </div>
              </div>
              <div class="level-right"></div>
            </div>
          </div>
        </div>
        <p v-else>{{errorMessage}}</p>
        <hr />
        <StepPreviousNext is-last v-bind:forStep="4" v-on:setStep="setStep" />
      </div>
    </div>
  </div>
</template>

<style scoped>
div.missing-input-columns {
  margin-bottom: 1em;
}
p.padded {
  padding-bottom: 0.5em;
}

p.breatheTop {
  padding-top: 0.25em;
}

label.breatheTop {
  padding-top: 0.75em;
}

div.breatheTop {
  margin-top: 2em;
}

h2.breatheTop {
  margin-top: 1em;
}

.form-top-title {
  margin-top: 1.5em;
}

.my-simulation-section {
  margin-bottom: 2em;
}

label.my-margin-top {
  margin-top: 0.75em;
}

.extraMarginTop {
  margin-top: 2.1em;
}

ul.my-steps {
  margin-top: 4em;
  margin-bottom: 4em;
}
.my-half-space {
  padding-top: 0.5em;
}

ul.missing-inputs {
  list-style: disc inside;
  padding-top: 0.5em;
  padding-bottom: 0.25em;
  padding-left: 1em;
}

ul.missing-inputs > li {
  padding-bottom: 0.25em;
}

p.missing-inputs {
  padding: 0.5em 0 0.5em 1em;
}

div > p.desc:not(:last-child) {
  border-bottom: 1px solid #e6e6e6;
  padding-bottom: 0.25em;
  margin-bottom: 0.25em;
}

p.desc > span:first-child {
  display: inline-block;
  width: 45%;
}
</style>

<script>
import config from '@/config/apollo-config';
import gql from 'graphql-tag';
import Coil from '@/components/Simulation/Coil.vue';
import Field from '@/components/Simulation/Field.vue';
import FieldRadio from '@/components/Simulation/FieldRadio.vue';
import FieldSelect from '@/components/Simulation/FieldSelect.vue';
import Library from '@/components/Simulation/Library.vue';
import Probes from '@/components/Simulation/Probes.vue';
import StepIndicator from '@/components/Simulation/StepIndicator.vue';
import StepPreviousNext from '@/components/Simulation/StepPreviousNext.vue';
import UploadGeometry from '@/components/Simulation/UploadGeometry.vue';
import Loader from '@/components/Loader.vue';
import apiCall from '../lib/remote-calls';
import NumberOrNull from '../lib/formats';

export default {
  components: {
    Coil,
    Field,
    FieldRadio,
    FieldSelect,
    Library,
    Probes,
    StepIndicator,
    StepPreviousNext,
    UploadGeometry,
    Loader,
  },
  data() {
    return {
      subscription_status: '',
      tokens_available: 0,
      errorMessage: '',
      canCreateSimulation: false,
      modelFile: '',
      simTitle: '',
      simDescription: '',
      activeStep: 0,
      deviceMaterialName: '',
      deviceDensity: '',
      deviceConductivity: '',
      devicePermittivity: '',
      deviceThermalConductivity: '',
      deviceHeatCapacity: '',
      deviceLocation: ['', '', ''],
      deviceOptions: [
        {
          value: 'UploadGeometry',
          label: 'Upload from File',
          id: 'step1-device-geometry-upload',
        },
        {
          value: 'Library',
          label: 'Select from Library',
          id: 'step1-device-geometry-library',
        },
      ],
      optionDevice: 'UploadGeometry',
      locationOptions: [
        {
          value: 'Default',
          label: 'Default',
          id: 'step1-device-location-default',
        },
        { value: 'Custom', label: 'Custom', id: 'step1-device-location-custom' },
      ],
      optionLocation: 'Default',
      optionGeometry: 0,
      optionStent: 'single',
      optionInsulation: 0,
      optionTip: 0,
      optionPower: 0,
      powerOptions: [2, 4, 10],
      optionPolarization: 0,
      polarizationOptions: ['CP1', 'CP2', 'LP1', 'LP2'],
      insulationOneMaterialName: '',
      insulationOneThickness: '',
      insulationOneDensity: '',
      insulationOneConductivity: '',
      insulationOnePermittivity: '',
      insulationOneThermalConductivity: '',
      insulationOneHeatCapacity: '',
      insulationTwoThickness: '',
      rodDiameter: '',
      rodLength: '',
      rodPitch: '',
      rodTipOne: '',
      rodTipTwo: '',
      stentOneDiameter: '',
      stentOneLength: '',
      stentOneThickness: '',
      stentTwoOverlap: '',
      stentTwoDiameter: '',
      stentTwoLength: '',
      stentTwoThickness: '',
      materials: [],
      coilGeometricProperties: [
        { diameter: 420, length: 650 },
        { diameter: 610, length: 620 },
      ],
      thermalOptions: [
        { id: 0, name: '240' },
        { id: 1, name: '300' },
        { id: 2, name: '360' },
        { id: 3, name: '420' },
        { id: 4, name: '480' },
        { id: 5, name: '540' },
        { id: 6, name: '600' },
        { id: 7, name: '660' },
        { id: 8, name: '720' },
        { id: 9, name: '780' },
        { id: 10, name: '840' },
        { id: 11, name: '900' },
      ],
      referenceRodChecked: false,
      referenceRodOptions: ['Hollow Rod', 'Rod'],
      referenceRodDiameter: '',
      referenceRodThickness: '',
      referenceRodLength: '',
      referenceRodLocation: ['', '', ''],
      referenceRodMaterialName: '',
      referenceRodDensity: '',
      referenceRodConductivity: '',
      referenceRodPermittivity: '',
      referenceRodHeatCapacity: '',
      referenceRodThermalConductivity: '',
      referenceRodName: 'Rod',
      optionFrequency: 0,
      optionThermal: '900',
      frequencyOptions: [64, 128],
      isLoading: false,
      probeCoordinates: null,
      phantomX: 210,
      phantomY: 45,
      phantomZ: 325,
      missingEntries: {
        0: [],
        1: [],
        2: [],
        3: [],
      }, // per activeStep
      isValidForm: false,
      isSubmitting: false,
    };
  },
  methods: {
    onGeometry(value) {
      this.optionGeometry = value;
    },
    onStent(value) {
      this.optionStent = value;
    },
    onInsulation(value) {
      this.optionInsulation = value;
    },
    onTip(value) {
      this.optionTip = value;
    },
    setStep(step) {
      this.activeStep = step;
      if (this.activeStep === 4) {
        const isValid = this.validateForm();
        this.isValidForm = isValid;
      }
    },
    nextStep() {
      this.setStep(this.activeStep + 1);
    },
    onFrequency(value) {
      this.optionFrequency = value;
    },
    onPower(value) {
      this.optionPower = value;
    },
    onPolarization(value) {
      this.optionPolarization = value;
    },
    updateProbes(value) {
      this.probeCoordinates = value;
    },
    async requestMaterials() {
      this.isLoading = true;
      try {
        const response = await apiCall('GET', 'materials');
        this.materials = response.data.materials;
        this.materials.unshift({
          id: 0,
          status: 0,
          name: 'custom',
          density: '',
          conductivity: '',
          permittivity: '',
          thermal_conductivity: '',
          heat_capacity: '',
        });
      } catch (error) {
        console.error(error);
      }
      this.isLoading = false;
    },
    optionThermalSelect(value) {
      this.optionThermal = value;
    },
    deviceMaterialSelect(materialName) {
      const mat = this.materials.find((el) => el.name === materialName);
      this.deviceMaterialName = mat.name;
      this.deviceDensity = mat.density;
      this.deviceConductivity = mat.conductivity;
      this.devicePermittivity = mat.permittivity;
      this.deviceThermalConductivity = mat.thermal_conductivity;
      this.deviceHeatCapacity = mat.heat_capacity;
    },
    insulationOneMaterialSelect(materialName) {
      const mat = this.materials.find((el) => el.name === materialName);
      this.insulationOneMaterialName = mat.name;
      this.insulationOneDensity = mat.density;
      this.insulationOneConductivity = mat.conductivity;
      this.insulationOnePermittivity = mat.permittivity;
      this.insulationOneThermalConductivity = mat.thermal_conductivity;
      this.insulationOneHeatCapacity = mat.heat_capacity;
    },
    referenceRodMaterialSelect(materialName) {
      const mat = this.materials.find((el) => el.name === materialName);
      this.referenceRodMaterialName = mat.name;
      this.referenceRodDensity = mat.density;
      this.referenceRodConductivity = mat.conductivity;
      this.referenceRodPermittivity = mat.permittivity;
      this.referenceRodThermalConductivity = mat.thermal_conductivity;
      this.referenceRodHeatCapacity = mat.heat_capacity;
    },
    fileUpload(fileObj) {
      this.modelFile = fileObj;
    },
    async submitSimulation() {
      const inputData = {};

      // title and description

      inputData.title = this.simTitle;
      inputData.description = this.simDescription;

      // phantom

      inputData.Phantom = {
        Position: {
          Y: 0,
          X: 0,
          Z: 0,
        },
        MaxRis: 5,
        Material: {
          Th_Cond: 0.42,
          Cond: 0.46,
          Sp_Heat: 4150,
          Perm: 80,
          Density: 1000,
        },
        Size: {
          T: 90,
          W: 420,
          L: 650,
        },
      };

      // reference rod

      if (this.referenceRodChecked === true) {
        inputData.RefRod = {
          Position: {
            X: NumberOrNull(this.referenceRodLocation[0]),
            Y: NumberOrNull(this.referenceRodLocation[1]),
            Z: NumberOrNull(this.referenceRodLocation[2]),
          },
          Material: {
            Name: this.referenceRodMaterialName,
            Th_Cond: NumberOrNull(this.referenceRodThermalConductivity),
            Cond: NumberOrNull(this.referenceRodConductivity),
            Sp_Heat: NumberOrNull(this.referenceRodHeatCapacity),
            Perm: NumberOrNull(this.referenceRodPermittivity),
            Density: NumberOrNull(this.referenceRodDensity),
          },
          Name: this.referenceRodName,
          Size: {
            L: NumberOrNull(this.referenceRodLength),
            T: NumberOrNull(this.referenceRodThickness),
            D: NumberOrNull(this.referenceRodDiameter),
          },
        };
      } else {
        inputData.RefRod = null;
      }

      // stent, rod, or upload

      if (this.optionDevice === 'Library') {
        if (this.optionGeometry === 0) {
          // Stent
          let stentNumber = 1;
          let stentTwoObject = null;
          if (this.optionStent === 'double') {
            stentNumber = 2;
            stentTwoObject = {
              L: NumberOrNull(this.stentTwoLength),
              T: NumberOrNull(this.stentTwoThickness),
              D: NumberOrNull(this.stentTwoDiameter),
            };
          }
          inputData.Stent = {
            Number: stentNumber,
            Stent1: {
              L: NumberOrNull(this.stentOneLength),
              T: NumberOrNull(this.stentOneThickness),
              D: NumberOrNull(this.stentOneDiameter),
            },
            Insulation1_T: NumberOrNull(this.insulationOneThickness),
            Stent2: stentTwoObject,
            Insulation2_T: NumberOrNull(this.insulationTwoThickness),
            Overlap: NumberOrNull(this.stentTwoOverlap),
          };
          inputData.Rod = null;
          inputData.Import = null;
        } else if (this.optionGeometry === 1) {
          // Rod
          inputData.Rod = {
            D: NumberOrNull(this.rodDiameter),
            L: NumberOrNull(this.rodLength),
            Insulation_T: NumberOrNull(this.insulationOneThickness),
            TipNumber:
              this.optionInsulation > 0 ? NumberOrNull(this.optionTip) : null,
            Tip1_L: NumberOrNull(this.rodTipOne),
            Tip2_L: NumberOrNull(this.rodTipTwo),
          };
          inputData.Stent = null;
          inputData.Import = null;
        } else {
          console.error('ERROR optionGeometry neither 0 (Stent) or 1 (Rod)');
        }
      } else if (this.optionDevice === 'UploadGeometry') {
        // Upload
        if (this.modelFile === '') {
          inputData.Import = null;
          console.error('Error: this.modelFile should be set');
        } else {
          inputData.Import = this.modelFile.name;
        }
        inputData.Stent = null;
        inputData.Rod = null;
      } else {
        console.error('ERROR optionDevice neither Library nor UploadGeometry');
      }

      // sim setup

      inputData.SimSetup = {
        Polarization: this.polarizationOptions[this.optionPolarization],
        Tback: 22,
        Frequency: this.frequencyOptions[this.optionFrequency],
        TDuration: this.optionThermal,
        Power: this.powerOptions[this.optionPower],
      };

      // implant

      const implantName = this.optionDevice === 'UploadGeometry' ? 'Import' : this.deviceName;
      const implantMaterial = {
        Name: this.deviceMaterialName,
        Density: NumberOrNull(this.deviceDensity),
        Cond: NumberOrNull(this.deviceConductivity),
        Perm: NumberOrNull(this.devicePermittivity),
        Th_Cond: NumberOrNull(this.deviceThermalConductivity),
        Sp_Heat: NumberOrNull(this.deviceHeatCapacity),
      };

      let insulation = null;
      if (this.optionDevice === 'Library' && this.optionInsulation > 0) {
        insulation = {
          Name: this.insulationOneMaterialName,
          Th_Cond: NumberOrNull(this.insulationOneThermalConductivity),
          Cond: NumberOrNull(this.insulationOneConductivity),
          Sp_Heat: NumberOrNull(this.insulationOneHeatCapacity),
          Perm: NumberOrNull(this.insulationOnePermittivity),
          Density: NumberOrNull(this.insulationOneDensity),
        };
      }

      // device location

      let implantLocation = null;
      if (this.optionLocation === 'Default') {
        implantLocation = { X: 190, Y: -25, Z: 0 };
        const polarization = this.polarizationOptions[this.optionPolarization];
        if (polarization === 'LP2' || polarization === 'CP2') {
          implantLocation.X = -190;
        }
      } else {
        // custom location
        implantLocation = {
          X: NumberOrNull(this.deviceLocation[0]),
          Y: NumberOrNull(this.deviceLocation[1]),
          Z: NumberOrNull(this.deviceLocation[2]),
        };
      }

      inputData.Implant = {
        Name: implantName,
        Material: implantMaterial,
        Volume: 10,
        MaxRis: 1,
        MaxMesh: 1,
        Insulation: insulation,
        Position: implantLocation,
      };

      // probes

      if (this.probeCoordinates === null) {
        inputData.Probes = null;
      } else { // copying the data because otherwise it would carry some Vue reactivity attributes
        const probes = [];
        for (let index = 0; index < this.probeCoordinates.length; index += 1) {
          const p = this.probeCoordinates[index];
          probes.push({ X: NumberOrNull(p.X), Y: NumberOrNull(p.Y), Z: NumberOrNull(p.Z) });
        }
        inputData.Probes = probes;
      }

      // debug inputs
      // console.log('INPUTS\n', inputData);

      // only available during E2E tests
      if (window.Cypress) {
        window.simInputs = inputData;
      }

      // send request
      this.isSubmitting = true;
      try {
        const json = JSON.stringify(inputData);
        const blob = new Blob([json], {
          type: 'application/json',
        });
        const formData = new FormData();
        formData.append('document', blob);
        formData.append('file', this.modelFile);
        await apiCall('POST', 'simulations', {
          data: formData,
          header: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });
        await this.$apollo.mutate({
          mutation: gql`
          mutation addNumrisJobAndTasks($product_name: ProductName!, $tasks: [NumrisTaskIn]!, $job_name: String!) {
            job: addNumrisJobAndTasks(product_name: $product_name, tasks: $tasks, job_name: $job_name) {
              _id
            }
          }`,
          variables: {
            product_name: config.productName,
            tasks: [{ title: inputData.title }],
            job_name: inputData.title,
          },
        });
        this.$router.push('/home');
      } catch (error) {
        console.error(error);
      }
    },
    async requestAccountDetails() {
      this.isAccountLoading = true;
      try {
        // only available during E2E tests
        if (window.Cypress) {
          this.canCreateSimulation = true;
        }
        const response = await this.$apollo.query({
          query: gql`
          query getUserSubscription($product_name: ProductName!) {
            account: getUserSubscription(product_name: $product_name) {
              product_name
              total_tokens
              tokens_used
              tokens_available
              status
              expiration_datetime
            }
          }`,
          variables: {
            product_name: config.productName,
          },
        });
        const { account } = response.data;
        if (account != null) {
          this.subscription_status = account.status;
          this.tokens_available = account.tokens_available;
          if (this.tokens_available > 0) {
            this.canCreateSimulation = true;
          } else {
            this.errorMessage = 'Please get in touch with a commercial representative for tokens to run simulations.';
          }
        } else {
          this.errorMessage = 'No subscription information found. Please get in touch with a commercial representative.';
        }
      } catch (error) {
        console.error(error);
      }
      this.isAccountLoading = false;
    },
    validateForm() {
      // As we allow the user to browse the simulation steps freely, we wait
      // until the user enters the submission step before checking for missing
      // inputs. The check is performed below.
      this.missingEntries = {
        0: [],
        1: [],
        2: [],
        3: [],
      };
      let isValid = true;

      // Step 0. Device Setup.

      if (this.simTitle === '') {
        this.missingEntries[0].push('Title');
        isValid = false;
      }

      if (this.optionDevice === 'UploadGeometry' && this.modelFile === '') {
        this.missingEntries[0].push('Model File');
        isValid = false;
      }

      if (this.optionDevice === 'Library') {
        if (this.optionGeometry === 0) {
          // Stent
          if (
            this.optionStent === 'single'
            && (NumberOrNull(this.stentOneDiameter) === null
              || NumberOrNull(this.stentOneLength) === null
              || NumberOrNull(this.stentOneThickness) === null)
          ) {
            this.missingEntries[0].push('Stent 1 Geometric Properties');
            isValid = false;
          }
          if (
            this.optionStent === 'double'
            && (NumberOrNull(this.stentTwoDiameter) === null
              || NumberOrNull(this.stentTwoLength) === null
              || NumberOrNull(this.stentTwoThickness) === null
              || NumberOrNull(this.stentTwoOverlap) === null)
          ) {
            this.missingEntries[0].push('Stent 2 Geometric Properties');
            isValid = false;
          }
        } else if (this.optionGeometry === 1) {
          // Rod
          if (
            NumberOrNull(this.rodDiameter) === null
            || NumberOrNull(this.rodLength) === null
          ) {
            this.missingEntries[0].push('Rod Geometric Properties');
            isValid = false;
          }
          if (this.optionInsulation > 0) {
            if (this.optionTip === 1 && NumberOrNull(this.rodTipOne) === null) {
              this.missingEntries[0].push('Tip 1 Length');
              isValid = false;
            }
            if (this.optionTip === 2 && NumberOrNull(this.rodTipTwo) === null) {
              this.missingEntries[0].push('Tip 2 Length');
              isValid = false;
            }
          }
        } else {
          console.error('unknown optionGeometry', this.optionGeometry);
          return null;
        }

        if (
          (this.optionInsulation === 1 || this.optionInsulation === 3)
          && NumberOrNull(this.insulationOneThickness) === null
        ) {
          this.missingEntries[0].push('Insulation');
          isValid = false;
        }

        if (
          (this.optionInsulation === 2 || this.optionInsulation === 3)
          && NumberOrNull(this.insulationTwoThickness) === null
        ) {
          this.missingEntries[0].push('Insulation');
          isValid = false;
        }
      }

      if (
        this.optionLocation === 'Custom'
        && (NumberOrNull(this.deviceLocation[0]) === null
          || NumberOrNull(this.deviceLocation[1]) === null
          || NumberOrNull(this.deviceLocation[2]) === null)
      ) {
        this.missingEntries[0].push('Device Location');
        isValid = false;
      }

      // Step 1. Device Material.

      if (NumberOrNull(this.deviceDensity) === null
          || NumberOrNull(this.deviceConductivity) === null
          || NumberOrNull(this.devicePermittivity) === null
          || NumberOrNull(this.deviceThermalConductivity) === null
          || NumberOrNull(this.deviceHeatCapacity) === null
      ) {
        this.missingEntries[1].push('Device Material');
        isValid = false;
      }

      if (
        this.optionInsulation > 0
        && (NumberOrNull(this.insulationOneDensity) === null
          || NumberOrNull(this.insulationOneConductivity) === null
          || NumberOrNull(this.insulationOnePermittivity) === null
          || NumberOrNull(this.insulationOneThermalConductivity) === null
          || NumberOrNull(this.insulationOneHeatCapacity) === null)
      ) {
        this.missingEntries[1].push('Insulation Material');
        isValid = false;
      }

      // Step 2. Simulation Setup.

      if (this.referenceRodChecked) {
        if (
          NumberOrNull(this.referenceRodDiameter) === null
          || NumberOrNull(this.referenceRodLength) === null
          || (this.referenceRodName === 'Hollow Rod'
            && NumberOrNull(this.referenceRodThickness) === null)
        ) {
          this.missingEntries[2].push('Reference Rod Geometric Properties');
          isValid = false;
        }

        if (
          NumberOrNull(this.referenceRodDensity) === null
          || NumberOrNull(this.referenceRodConductivity) === null
          || NumberOrNull(this.referenceRodPermittivity) === null
          || NumberOrNull(this.referenceRodThermalConductivity) === null
          || NumberOrNull(this.referenceRodHeatCapacity) === null
        ) {
          this.missingEntries[2].push('Reference Rod Material Properties');
          isValid = false;
        }

        if (
          NumberOrNull(this.referenceRodLocation[0]) === null
          || NumberOrNull(this.referenceRodLocation[1]) === null
          || NumberOrNull(this.referenceRodLocation[2]) === null
        ) {
          this.missingEntries[2].push('Reference Rod Geometric Properties');
          isValid = false;
        }
      }

      // Step 3. Probes.
      if (this.probeCoordinates !== null) {
        for (let index = 0; index < this.probeCoordinates.length; index += 1) {
          const coords = this.probeCoordinates[index];
          if (
            NumberOrNull(coords.X) === null
            || NumberOrNull(coords.Y) === null
            || NumberOrNull(coords.Z) === null
          ) {
            this.missingEntries[3].push('Probe Coordinates');
            isValid = false;
            break;
          }
        }
      }

      return isValid;
    },
  },
  mounted() {
    this.requestMaterials();
    this.requestAccountDetails();
  },
  computed: {
    deviceName() {
      if (this.optionDevice === 'UploadGeometry') {
        return 'Device';
      }
      if (this.optionDevice === 'Library') {
        if (this.optionGeometry === 0) {
          return 'Stent';
        }
        if (this.optionGeometry === 1) {
          return 'Rod';
        }
        return 'Unknown';
      }
      return 'Unknown';
    },
    extendedDeviceName() {
      if (this.optionDevice === 'UploadGeometry') {
        return this.modelFile.name;
      }
      if (this.optionDevice === 'Library') {
        if (this.optionGeometry === 0) {
          if (this.optionStent === 'single') {
            return 'Stent';
          }
          return 'Double Stent'; // optionStent === 'double'
        }
        if (this.optionGeometry === 1) {
          return 'Rod';
        }
        return 'Unknown';
      }
      return 'Unknown';
    },
    deviceGeometry() {
      const geometries = [];
      if (this.optionGeometry === 0) {
        geometries.push(['Geometry', `D=${this.stentOneDiameter}; L=${this.stentOneLength}; T=${this.stentOneThickness} mm`]);
        if (this.optionStent === 'double') {
          geometries.push(['Geometry (2)', `D=${this.stentTwoDiameter}; L=${this.stentTwoLength}; T=${this.stentTwoThickness} mm`]);
        }
      }
      if (this.optionGeometry === 1) {
        geometries.push(['Geometry', `D=${this.rodDiameter}; L=${this.rodLength} mm`]);
      }
      return geometries;
    },
    deviceLocationStr() {
      if (this.optionLocation === 'Custom') {
        return `X=${this.deviceLocation[0]}; Y=${this.deviceLocation[1]}; Z=${this.deviceLocation[2]}`;
      }
      return 'Default';
    },
    referenceRodGeometry() {
      let geometry = `D=${this.referenceRodDiameter}; L=${this.referenceRodLength}`;
      if (this.referenceRodName === 'Hollow Rod') {
        geometry += `; T=${this.referenceRodThickness}`;
      }
      geometry += ' mm';
      return geometry;
    },
    referenceRodPosition() {
      return `X=${this.referenceRodLocation[0]}; Y=${this.referenceRodLocation[1]}; Z=${this.referenceRodLocation[2]}`;
    },
    tipOneLengthLabel() {
      if (this.optionTip > 1) {
        return 'Tip 1 Length';
      }
      return 'Tip Length';
    },
    insulationTitle() {
      const title = 'Insulation';
      /*
      if (this.optionDevice === "Library" && this.optionGeometry === 0) {
        title += " 1";
      }
      */
      return title;
    },
    rodTipOneMax() {
      const l = parseFloat(this.rodLength) || 1;
      return l - 1;
    },
    rodTipTwoMax() {
      const l = parseFloat(this.rodLength) || 1;
      const t1 = parseFloat(this.rodTipOne) || 0;
      const remaining = l - t1 - 1.0;
      return remaining;
    },
    stentTwoOverlapMax() {
      return parseFloat(this.stentOneLength);
    },
    stentTwoDiameterMin() {
      const stentOneDiameter = parseFloat(this.stentOneDiameter) || 1;
      const stentOneInsulation = this.optionInsulation === 0 || this.optionInsulation === 2
        ? 0
        : parseFloat(this.insulationOneThickness) || 0.5;
      const stentTwoThickness = parseFloat(this.stentTwoThickness) || 0.05;
      return parseFloat(
        (
          2 * (stentOneDiameter / 2 + stentOneInsulation + stentTwoThickness)
          + 0.05
        ).toPrecision(3),
      );
    },
    referenceRodMaxThickness() {
      return parseFloat(this.referenceRodDiameter) / 2 - 0.25;
    },
    customLocationBoundaryX() {
      if (this.optionDevice === 'Library') {
        if (this.optionGeometry === 0) {
          // Stent
          const d1 = parseFloat(this.stentOneDiameter) || 0;
          const d2 = parseFloat(this.stentTwoDiameter) || 0;
          const i1 = parseFloat(this.insulationOneThickness) || 0;
          const i2 = parseFloat(this.insulationTwoThickness) || 0;
          return this.phantomX - (d1 / 2 + i1 + d2 / 2 + i2);
        }
        if (this.optionGeometry === 1) {
          // Rod
          const d = parseFloat(this.rodDiameter) || 0;
          const i = parseFloat(this.insulationOneThickness) || 0;
          return this.phantomX - (d / 2 + i);
        }
        return null;
      }
      // Uploaded
      return this.phantomX;
    },
    customLocationBoundaryY() {
      if (this.optionDevice === 'Library') {
        if (this.optionGeometry === 0) {
          // Stent
          const d1 = parseFloat(this.stentOneDiameter) || 0;
          const d2 = parseFloat(this.stentTwoDiameter) || 0;
          const i1 = parseFloat(this.insulationOneThickness) || 0;
          const i2 = parseFloat(this.insulationTwoThickness) || 0;
          return this.phantomY - (d1 / 2 + i1 + d2 / 2 + i2);
        }
        if (this.optionGeometry === 1) {
          // Rod
          const d = parseFloat(this.rodDiameter) || 0;
          const i = parseFloat(this.insulationOneThickness) || 0;
          return this.phantomY - (d / 2 + i);
        }
        return null;
      }
      // Uploaded
      return this.phantomY;
    },
    customLocationBoundaryZ() {
      if (this.optionDevice === 'Library') {
        if (this.optionGeometry === 0) {
          // Stent
          const l1 = parseFloat(this.stentOneLength) || 0;
          const l2 = parseFloat(this.stentTwoLength) || 0;
          const o = parseFloat(this.stentTwoOverlap) || 0;
          return this.phantomZ - (l1 / 2 + l2 / 2 - o);
        }
        if (this.optionGeometry === 1) {
          // Rod
          const l = parseFloat(this.rodLength) || 0;
          return this.phantomZ - l / 2;
        }
        return null;
      }
      // Uploaded
      return this.phantomZ;
    },
    referenceRodBoundaryX() {
      const d = parseFloat(this.referenceRodDiameter) || 0;
      return this.phantomX - d / 2;
    },
    referenceRodBoundaryY() {
      const d = parseFloat(this.referenceRodDiameter) || 0;
      return this.phantomY - d / 2;
    },
    referenceRodBoundaryZ() {
      const l = parseFloat(this.referenceRodLength) || 0;
      return this.phantomZ - l / 2;
    },
  },
};
</script>
